import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { ShieldStarIcon as ShieldStar, ArrowsLeftRightIcon as ArrowsLeftRight, GitForkIcon as GitFork, FlagIcon as Flag, ArrowRightIcon as ArrowRight, CheckCircleIcon as CheckCircle, ClockIcon as Clock, SealCheckIcon as SealCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

/**
 * Admin "Approvals" — single page that pulls every outstanding admin
 * action into one dashboard so we don't have to bounce between tabs to
 * know what's waiting. Each section shows a count, the latest 5 rows,
 * and a deep-link to the dedicated queue page (where decisions are
 * actually made).
 */
export default function AdminApprovals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/approvals");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const totals = data?.totals || { transfers: 0, pedigree_additions: 0, kennel_verifications: 0, post_reports: 0, provider_apps: 0, transporter_apps: 0, intl_courier_apps: 0, intl_pro_apps: 0, chat_reports: 0, kennel_claims: 0, total: 0 };
  const queues = data?.queues || {};

  return (
    <AppShell>
      <div className="space-y-8" data-testid="admin-approvals-page">
        <header className="flex items-end justify-between flex-wrap gap-4 pb-5 relative after:absolute after:left-0 after:right-0 after:bottom-0 after:h-px after:bg-gold-hairline">
          <div>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-gold font-bold">
              <ShieldStar size={14} weight="duotone" /> Admin
            </div>
            <h1 className="font-spectral font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] mt-3">Approvals</h1>
            <p className="text-sm text-[#6B6B6B] mt-3 max-w-2xl leading-relaxed">
              Everything waiting on you, in one place. Click any section to
              act on items individually.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold font-tabular ${
                totals.total > 0
                  ? "bg-cream text-[#8A6A00] border border-gold/40"
                  : "bg-[#F0F8F1] text-[#0F8043] border border-[#0F8043]/30"
              }`}
              data-testid="approvals-total-badge"
            >
              {totals.total > 0 ? (
                <>
                  <Clock size={13} weight="duotone" /> {totals.total} pending
                </>
              ) : (
                <>
                  <CheckCircle size={13} weight="duotone" /> All clear
                </>
              )}
            </span>
            <Button
              onClick={load}
              variant="outline"
              className="rounded-full border-[#E5E5E5]"
              disabled={loading}
              data-testid="approvals-refresh-btn"
            >
              {loading ? "Refreshing…" : "Refresh"}
            </Button>
          </div>
        </header>

        <ApprovalSection
          title="Ownership transfers"
          subtitle="Buyer has paid — your sign-off finalizes the transfer."
          icon={ArrowsLeftRight}
          count={totals.transfers}
          rows={queues.transfers || []}
          renderRow={(r) => (
            <TransferRow key={r.id} row={r} onChange={load} />
          )}
          emptyText="No transfers waiting on you."
          openHref="/admin/transfers"
          testId="section-transfers"
        />

        <ApprovalSection
          title="Pedigree additions"
          subtitle="User-submitted ancestors awaiting verification."
          icon={GitFork}
          count={totals.pedigree_additions}
          rows={queues.pedigree_additions || []}
          renderRow={(r) => (
            <PedigreeRow key={r.id} row={r} navigate={navigate} />
          )}
          emptyText="No pedigree additions in the queue."
          openHref="/admin/pedigree-additions"
          testId="section-pedigree"
        />

        <ApprovalSection
          title="Kennel verifications"
          subtitle="Verified-kennel packets awaiting review (identity, premises, references)."
          icon={SealCheck}
          count={totals.kennel_verifications}
          rows={queues.kennel_verifications || []}
          renderRow={(r) => (
            <KennelRow key={r.breeder_id || r.id} row={r} navigate={navigate} />
          )}
          emptyText="No kennel verifications waiting on you."
          openHref="/breeders"
          openLabel="Open all breeders"
          testId="section-kennel-verifications"
        />

        <ApprovalSection
          title="Service provider applications"
          subtitle="Trainers, groomers, walkers & sitters awaiting your review."
          icon={ShieldStar}
          count={totals.provider_apps}
          rows={queues.provider_apps || []}
          renderRow={(r) => (
            <ProviderAppRow key={r.id} row={r} onChange={load} />
          )}
          emptyText="No provider applications waiting on you."
          openHref="/admin/providers"
          testId="section-provider-apps"
        />

        <ApprovalSection
          title="Transporter applications"
          subtitle="US ground transporters awaiting credential review."
          icon={ShieldStar}
          count={totals.transporter_apps}
          rows={queues.transporter_apps || []}
          renderRow={(r) => <SimpleAppRow key={r.id} name={r.business_name || r.legal_name} email={r.email} sub={r.review_status} createdAt={r.created_at} />}
          emptyText="No transporter applications waiting on you."
          openHref="/admin/transporters"
          testId="section-transporter-apps"
        />

        <ApprovalSection
          title="International courier applications"
          subtitle="Foreign couriers who paid the $9 verification fee — approve or reject."
          icon={ShieldStar}
          count={totals.intl_courier_apps}
          rows={queues.intl_courier_apps || []}
          renderRow={(r) => <SimpleAppRow key={r.id} name={r.display_name} email={r.email} sub={`${r.home_country || ""} · ${(r.corridors || []).slice(0, 2).join(", ")}`} createdAt={r.created_at} />}
          emptyText="No courier applications waiting on you."
          openHref="/admin/intl-couriers"
          testId="section-intl-courier-apps"
        />

        <ApprovalSection
          title="Intl service-pro applications"
          subtitle="International walkers, sitters, groomers & trainers awaiting review."
          icon={ShieldStar}
          count={totals.intl_pro_apps}
          rows={queues.intl_pro_apps || []}
          renderRow={(r) => <SimpleAppRow key={r.id} name={r.display_name} email={r.email} sub={`${r.service_kind || ""} · ${r.home_country || ""}`} createdAt={r.created_at} />}
          emptyText="No intl service-pro applications waiting on you."
          openHref="/admin/intl-ops"
          testId="section-intl-pro-apps"
        />

        <ApprovalSection
          title="Kennel claim requests"
          subtitle="Members claiming an unclaimed kennel listing — approving links the kennel to their account."
          icon={SealCheck}
          count={totals.kennel_claims}
          rows={queues.kennel_claims || []}
          renderRow={(r) => <KennelClaimRow key={r.id} row={r} onChange={load} />}
          emptyText="No kennel claims waiting on you."
          openHref="/kennels"
          openLabel="Open kennels directory"
          testId="section-kennel-claims"
        />

        <ApprovalSection
          title="Reported chat conversations"
          subtitle="Registry Chat threads reported by a participant — transcripts unlock only after a report."
          icon={Flag}
          count={totals.chat_reports}
          rows={queues.chat_reports || []}
          renderRow={(r) => <SimpleAppRow key={r.id} name={(r.participant_names || []).join(" ↔ ")} email={r.latest_reason} sub="" createdAt={r.last_message_at} />}
          emptyText="No reported conversations."
          openHref="/admin/chat-reports"
          testId="section-chat-reports"
        />

        <ApprovalSection
          title="Reported posts"
          subtitle="Posts on The Dog's Voice flagged by the community."
          icon={Flag}
          count={totals.post_reports}
          rows={queues.post_reports || []}
          renderRow={(r) => (
            <ReportRow key={r.id} row={r} onChange={load} />
          )}
          emptyText="No outstanding reports."
          openHref="/feed"
          openLabel="Open The Dog's Voice feed"
          testId="section-reports"
        />
      </div>
    </AppShell>
  );
}

// ---------- Section shell ----------------------------------------------

function ApprovalSection({
  title,
  subtitle,
  icon: Icon,
  count,
  rows,
  renderRow,
  emptyText,
  openHref,
  openLabel = "Open queue",
  testId,
}) {
  return (
    <section
      className="bg-white border border-[#EAEAEA] rounded-2xl"
      data-testid={testId}
    >
      <div className="flex items-start justify-between p-5 border-b border-[#F0F0F0]">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#FBF7EC] flex items-center justify-center flex-shrink-0">
            <Icon size={20} weight="duotone" className="text-gold" />
          </div>
          <div>
            <h2 className="font-spectral text-xl flex items-center gap-2">
              {title}
              {count > 0 && (
                <span
                  className="inline-flex items-center justify-center bg-[#C1272D] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1.5"
                  data-testid={`${testId}-count`}
                >
                  {count}
                </span>
              )}
            </h2>
            <p className="text-xs text-[#6B6B6B] mt-0.5">{subtitle}</p>
          </div>
        </div>
        <Link
          to={openHref}
          className="inline-flex items-center gap-1 text-xs font-semibold text-ink hover:text-crimson"
          data-testid={`${testId}-open-link`}
        >
          {openLabel} <ArrowRight size={12} weight="bold" />
        </Link>
      </div>
      <div>
        {rows.length === 0 ? (
          <div className="px-5 py-8 text-sm text-[#6B6B6B] flex items-center gap-2">
            <CheckCircle size={14} weight="duotone" className="text-[#0F8043]" />
            {emptyText}
          </div>
        ) : (
          <ul className="divide-y divide-[#F0F0F0]">{rows.map(renderRow)}</ul>
        )}
      </div>
    </section>
  );
}

// ---------- Row variants ------------------------------------------------

function SimpleAppRow({ name, email, sub, createdAt }) {
  return (
    <li className="px-5 py-3 flex items-center gap-3">
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-semibold text-ink truncate">{name || "(unnamed)"}</span>
        <span className="block text-[11px] text-[#6B6B6B] truncate">{email}{sub ? ` · ${sub}` : ""}</span>
      </span>
      <span className="text-[10px] text-[#9A9A9A] flex-shrink-0">
        {createdAt ? new Date(createdAt).toLocaleDateString() : ""}
      </span>
    </li>
  );
}

function KennelClaimRow({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/breeders/claims/${row.id}/${action}`);
      toast.success(action === "approve" ? "Claim approved — kennel linked to the member." : "Claim rejected.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="px-5 py-4 flex items-center gap-3 flex-wrap" data-testid={`kennel-claim-row-${row.id}`}>
      <Link to={`/k/${row.breeder_id}`} className="flex-1 min-w-0 group">
        <div className="font-semibold text-sm truncate group-hover:text-crimson">
          {row.kennel_name || "(kennel)"}
        </div>
        <div className="text-xs text-[#6B6B6B] truncate">
          {row.user_name || "(member)"} · {row.user_email}
        </div>
        {row.message && (
          <div className="text-[11px] text-[#9A9A9A] truncate italic mt-0.5">&ldquo;{row.message}&rdquo;</div>
        )}
      </Link>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => act("approve")}
          disabled={busy}
          className="rounded-full bg-[#0F8043] hover:bg-[#0a6334] text-white text-xs h-8"
          data-testid={`kennel-claim-approve-${row.id}`}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => act("reject")}
          disabled={busy}
          className="rounded-full text-xs h-8"
          data-testid={`kennel-claim-reject-${row.id}`}
        >
          Reject
        </Button>
      </div>
    </li>
  );
}

function TransferRow({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const act = async (action) => {
    setBusy(true);
    try {
      await api.post(`/admin/transfers/${row.id}/${action}`);
      toast.success(action === "approve" ? "Transfer approved." : "Transfer rejected.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="px-5 py-4 flex items-center gap-3 flex-wrap" data-testid={`transfer-row-${row.id}`}>
      <Link
        to={`/dogs/${row.dog_id}`}
        className="flex-1 min-w-0 group"
      >
        <div className="font-semibold text-sm truncate group-hover:text-crimson">
          {row.dog_name || "(unknown dog)"}
        </div>
        <div className="text-xs text-[#6B6B6B] truncate">
          {row.from_name || "—"} → {row.to_name || "—"}
        </div>
      </Link>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => act("approve")}
          disabled={busy}
          className="rounded-full bg-[#0F8043] hover:bg-[#0a6334] text-white text-xs h-8"
          data-testid={`transfer-approve-${row.id}`}
        >
          Approve
        </Button>
        <Button
          size="sm"
          onClick={() => act("reject")}
          disabled={busy}
          variant="outline"
          className="rounded-full border-[#C1272D]/30 text-crimson text-xs h-8"
          data-testid={`transfer-reject-${row.id}`}
        >
          Reject
        </Button>
      </div>
    </li>
  );
}

function PedigreeRow({ row, navigate }) {
  // Backend denormalizes the dog name on submission as
  // `attached_dog_name` and now also returns a one-line `entries_summary`
  // (e.g. "DAM · Granados Holly  +1 more"). Fall back through the
  // legacy fields so this row stays readable for any rows submitted
  // before that hydration shipped.
  const dogName = row.attached_dog_name || row.dog_name || "(unknown)";
  const subtitle =
    row.entries_summary ||
    [row.relationship, row.proposed?.name || row.name].filter(Boolean).join(" · ") ||
    (row.entries_count ? `${row.entries_count} ancestor${row.entries_count === 1 ? "" : "s"}` : "—");
  return (
    <li className="px-5 py-4 flex items-center gap-3 flex-wrap" data-testid={`pedigree-row-${row.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">
          Ancestor for <span className="text-crimson">{dogName}</span>
        </div>
        <div className="text-xs text-[#6B6B6B] truncate">
          {subtitle}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => navigate("/admin/pedigree-additions")}
        className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8"
        data-testid={`pedigree-review-${row.id}`}
      >
        Review →
      </Button>
    </li>
  );
}

function KennelRow({ row, navigate }) {
  const passed = row.steps_passed ?? 0;
  const total = row.steps_total ?? 7;
  const breederId = row.breeder_id || row.id;
  return (
    <li className="px-5 py-4 flex items-center gap-3 flex-wrap" data-testid={`kennel-row-${breederId}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">
          {row.kennel_name || "(unknown kennel)"}
        </div>
        <div className="text-xs text-[#6B6B6B] truncate">
          {passed}/{total} steps passed · status: {row.status || "—"}
        </div>
      </div>
      <Button
        size="sm"
        onClick={() => navigate(`/breeders?verify=${breederId}`)}
        className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8"
        data-testid={`kennel-review-${breederId}`}
      >
        Review →
      </Button>
    </li>
  );
}


function ProviderAppRow({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const act = async (status) => {
    setBusy(true);
    try {
      await api.put(`/admin/providers/${row.id}/status`, { status, reason: null });
      toast.success(status === "approved" ? "Provider approved — welcome email sent." : "Application rejected.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const loc = row.location || {};
  return (
    <li className="px-5 py-4 flex items-center gap-3 flex-wrap" data-testid={`provider-app-row-${row.id}`}>
      <Link to="/admin/providers" className="flex-1 min-w-0 group">
        <div className="font-semibold text-sm truncate group-hover:text-crimson">
          {row.business_name || "(unnamed business)"}
        </div>
        <div className="text-xs text-[#6B6B6B] truncate">
          {row.kind || "provider"}
          {loc.city ? ` · ${loc.city}, ${loc.state || ""}` : ""}
          {row.email ? ` · ${row.email}` : ""}
        </div>
      </Link>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => act("approved")}
          disabled={busy}
          className="rounded-full bg-[#0F8043] hover:bg-[#0a6334] text-white text-xs h-8"
          data-testid={`provider-approve-${row.id}`}
        >
          Approve
        </Button>
        <Button
          size="sm"
          onClick={() => act("rejected")}
          disabled={busy}
          variant="outline"
          className="rounded-full border-[#C1272D]/30 text-crimson text-xs h-8"
          data-testid={`provider-reject-${row.id}`}
        >
          Reject
        </Button>
      </div>
    </li>
  );
}


function ReportRow({ row, onChange }) {
  const [busy, setBusy] = useState(false);
  const hide = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/posts/${row.post_id}/hide`);
      toast.success("Post hidden.");
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/posts/reports/${row.id}/handle`);
      toast.success("Report dismissed.");
      onChange?.();
    } catch (e) {
      // Endpoint may not exist yet — fall back to silently re-loading.
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="px-5 py-4 flex items-start gap-3 flex-wrap" data-testid={`report-row-${row.id}`}>
      <Flag size={18} weight="duotone" className="text-crimson mt-1 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm">
          {row.dog_name ? `On ${row.dog_name}'s post` : "On a post"}
          {row.post_hidden && (
            <span className="ml-2 inline-flex items-center px-1.5 h-4 text-[9px] font-bold rounded bg-[#0E0E0E] text-white">
              HIDDEN
            </span>
          )}
        </div>
        <div className="text-xs text-[#6B6B6B] mt-0.5 truncate">
          “{row.post_excerpt || "(no text)"}”
        </div>
        {row.reason && (
          <div className="text-[11px] text-crimson mt-1">
            Reason: {row.reason}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {!row.post_hidden && (
          <Button
            size="sm"
            onClick={hide}
            disabled={busy}
            className="rounded-full bg-ink hover:bg-crimson text-white text-xs h-8"
            data-testid={`report-hide-${row.id}`}
          >
            Hide post
          </Button>
        )}
        <Button
          size="sm"
          onClick={dismiss}
          disabled={busy}
          variant="outline"
          className="rounded-full border-[#E5E5E5] text-xs h-8"
          data-testid={`report-dismiss-${row.id}`}
        >
          Dismiss
        </Button>
      </div>
    </li>
  );
}
